import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import type { FIXED_REPORT_TARGET_KEYS, ReportGoalsResponse } from "@gioia365/contracts";
import { monthOf, shiftMonth } from "@gioia365/domain/report-goals";
import type { Locale } from "@gioia365/i18n";
import { useQuery } from "@tanstack/react-query";
import { ApiRequestError } from "../../api/http";
import { reportGoalsSettingsQuery, useSaveReportGoals, useTab } from "../../api/queries";
import { AboutBox } from "../../design/components/AboutBox";
import { Button } from "../../design/components/Button";
import { Reason } from "../../design/components/Reason";
import { SaveBar } from "../../design/components/SaveBar";
import { Select } from "../../design/components/Select";
import { SettingsLayout } from "../../design/components/SettingsLayout";
import { TextField } from "../../design/components/TextField";
import { useToast } from "../../design/components/Toast";
import { messageForError } from "../../i18n/errors";
import { useLocale, useT } from "../../i18n/locale";
import { targetNumber } from "../../reports/report-format";
import { useAccess } from "../../session/access";
import { ConfirmChangesDialog, draftSummary, LeaveDialog, useLeaveGuard, type Feedback } from "./draft-dialogs";
import {
  describeGoalsChanges,
  draftOf,
  goalsProblems,
  problemText,
  saveBodyOf,
  savedTextOf,
  TARGET_NAMES,
  type GoalsEdits,
  type GoalsProblem,
  type TargetKey,
  type TargetText,
} from "./goals-draft";
import "./goals.css";

// Impostazioni → «Obiettivi» (piano M7, Task 6; D9; SEZ-16, IMP-1, IMP-2; sul modello di «Assegnazione dei Lead»): gli obiettivi di
// vendita del mese persona per persona, il valore medio di una vendita e le percentuali con cui il Report colora i numeri e risale dalle
// vendite alle chiamate. Il mese (quello di oggi, quello prima e quello dopo); «Valore medio di una vendita»; una riga per Commerciale
// con «Obiettivo di vendita del mese»; «Obiettivi delle percentuali» con «Da» e «A», le due decise in sola lettura con «deciso, non si
// cambia». Una bozza (i soli campi toccati) con la barra delle modifiche, «Salva…» con «Cosa cambia per il team» e una sola richiesta con
// la versione letta; una versione cambiata nel frattempo (`409 version_conflict`, o la rilettura che la porta) rimette le modifiche
// sopra gli obiettivi nuovi e lo dice in cima. La sezione la vede soltanto chi ha `settings.goals` (di partenza il Super admin), perché
// mostra l'obiettivo in euro di ogni persona; il Super admin di Gioia365 la guarda senza cambiarla, come le altre regole dell'azienda
// (IMP-1). Con una bozza il mese non si cambia: gli obiettivi di un altro mese sono un'altra bozza.

export function GoalsPage() {
  return (
    <SettingsLayout section="goals">
      <GoalsSection />
    </SettingsLayout>
  );
}

/** Il nome del mese nella lingua della persona: «ottobre 2026», «October 2026». */
export function monthName(locale: Locale, month: string): string {
  return new Intl.DateTimeFormat(locale, { month: "long", year: "numeric", timeZone: "UTC" }).format(new Date(`${month}-15T12:00:00.000Z`));
}

function GoalsSection() {
  const t = useT();
  const tab = useTab();
  const access = useAccess();
  const timeZone = access.context.settings.timeZone;
  const today = useMemo(() => monthOf(new Date(), timeZone).month, [timeZone]);
  const [month, setMonth] = useState(today);
  // Chi non ha la sezione non legge niente (il server risponderebbe 403): la pagina dice perché.
  const readable = access.isPlatform || access.can("settings.goals");
  const goals = useQuery({ ...reportGoalsSettingsQuery(tab, month), enabled: readable, placeholderData: (previous) => previous });
  const organization = access.context.organization.name;
  const allowed = access.can("settings.goals") && !access.isPlatform;
  const editable = allowed && goals.data?.canEdit === true && goals.data.month === month;

  let body: ReactNode;
  if (!readable) {
    body = <Reason>{t("goalsSettings.noAccess")}</Reason>;
  } else if (goals.data) {
    body = <GoalsEditor key={goals.data.month} base={goals.data} editable={editable} month={month} months={[shiftMonth(today, -1), today, shiftMonth(today, 1)]} onMonth={setMonth} />;
  } else if (goals.isError) {
    body = (
      <div className="g-goals__problem">
        <Reason tone="warning" role="alert">
          {messageForError(t, goals.error)}
        </Reason>
        <Button onClick={() => void goals.refetch()}>{t("common.retry")}</Button>
      </div>
    );
  } else {
    body = <p className="g-goals__muted">{t("common.loading")}</p>;
  }

  return (
    <>
      <AboutBox title={t("goalsSettings.about")}>
        <p>{t("goalsSettings.aboutText")}</p>
      </AboutBox>
      {access.isPlatform ? <Reason>{t("goalsSettings.readOnlyPlatform", { organization })}</Reason> : null}
      {body}
    </>
  );
}

type EditorProps = { base: ReportGoalsResponse; editable: boolean; month: string; months: readonly string[]; onMonth: (month: string) => void };

function GoalsEditor({ base, editable, month, months, onMonth }: EditorProps) {
  const t = useT();
  const { locale } = useLocale();
  const tab = useTab();
  const toast = useToast();
  const saveGoals = useSaveReportGoals(tab);
  const baseId = useId();
  const [edits, setEdits] = useState<GoalsEdits>({});
  // La finestra «Cosa cambia per il team», con il rifiuto del server se il salvataggio si è fermato.
  const [confirming, setConfirming] = useState<{ error: Feedback | null } | null>(null);
  const [saving, setSaving] = useState(false);
  // Un conflitto di versione: la pagina lo dice sopra la bozza.
  const [notice, setNotice] = useState<Feedback | null>(null);
  // La versione su cui la bozza è stata pensata (il modello di «Assegnazione dei Lead»).
  const [draftVersion, setDraftVersion] = useState<number | null>(null);
  // Un salvataggio in corso, letto anche da un secondo clic che arriva prima del nuovo disegno.
  const savingNow = useRef(false);

  const saved = useMemo(() => savedTextOf(base, locale), [base, locale]);
  // Senza il permesso si vede il salvato, non una bozza che non si può salvare.
  const draft = useMemo(() => draftOf(saved, editable ? edits : {}), [saved, editable, edits]);
  const name = monthName(locale, base.month);
  const changes = describeGoalsChanges(base, draft, name, t, locale);
  const problems = editable ? goalsProblems(draft, base.people) : [];
  const dirty = editable && (changes.length > 0 || problems.length > 0);
  const blocker = useLeaveGuard(dirty);
  const names = new Map(base.people.map((row) => [row.person.membershipId, row.person.displayName]));
  const nameOf = (membershipId: string) => names.get(membershipId) ?? membershipId;
  const problemOf = (match: (problem: GoalsProblem) => boolean) => {
    const found = problems.find(match);
    return found ? problemText(t, found, nameOf) : undefined;
  };
  const firstProblem = problems[0];
  const hasEdits = editable && Object.keys(edits).length > 0;

  useEffect(() => {
    if (!hasEdits) {
      if (draftVersion !== null) setDraftVersion(null);
      return;
    }
    if (draftVersion === null) {
      setDraftVersion(base.version);
      return;
    }
    if (base.version === draftVersion) return;
    setDraftVersion(base.version);
    // Il proprio salvataggio rilegge gli obiettivi mentre salva: quello non è un conflitto.
    if (savingNow.current) return;
    setConfirming(null);
    setNotice({ text: (tr) => tr("goalsSettings.conflict") });
  }, [hasEdits, base.version, draftVersion]);

  const disabled = !editable || saving;

  function change(update: GoalsEdits) {
    if (!editable || savingNow.current) return;
    setEdits((current) => ({
      ...current,
      ...update,
      targets: { ...current.targets, ...update.targets },
      goals: { ...current.goals, ...update.goals },
    }));
  }

  function changeTarget(key: TargetKey, field: keyof TargetText, text: string) {
    change({ targets: { [key]: { ...draft.targets[key], [field]: text } } });
  }

  function discard() {
    setEdits({});
    setNotice(null);
    toast.show(t("settings.draft.discarded"));
  }

  async function save() {
    const body = saveBodyOf(base, draft, base.version);
    if (!editable || savingNow.current || body === null || changes.length === 0) return;
    savingNow.current = true;
    setSaving(true);
    setConfirming({ error: null });
    try {
      await saveGoals.mutateAsync(body);
      setConfirming(null);
      setEdits({});
      setNotice(null);
      toast.show(t("goalsSettings.saved"), "success");
    } catch (error) {
      if (error instanceof ApiRequestError && error.code === "version_conflict") {
        // Gli obiettivi si rileggono: le modifiche restano sopra quelli nuovi, da controllare.
        setConfirming(null);
        setNotice({ text: (tr) => tr("goalsSettings.conflict") });
      } else {
        setConfirming({ error: { text: (tr) => messageForError(tr, error) } });
      }
    } finally {
      savingNow.current = false;
      setSaving(false);
    }
  }

  function stay() {
    if (blocker.status === "blocked") blocker.reset();
  }

  function leave() {
    setEdits({});
    if (blocker.status === "blocked") blocker.proceed();
  }

  const fixed = (key: (typeof FIXED_REPORT_TARGET_KEYS)[number]) => {
    const target = base.targets[key];
    return target.max === undefined
      ? t("goalsSettings.atLeast", { min: targetNumber(locale, target.min) })
      : t("goalsSettings.range", { min: targetNumber(locale, target.min), max: targetNumber(locale, target.max) });
  };
  // Nell'ordine dei numeri del Report: disco su chiamate, su risposte, show up disco, demo su disco, show up demo, close rate.
  const targetRows: Array<{ key: TargetKey } | { fixed: (typeof FIXED_REPORT_TARGET_KEYS)[number] }> = [
    { fixed: "discoOnCalls" },
    { key: "discoOnAnswered" },
    { key: "discoShow" },
    { key: "demoOnDisco" },
    { fixed: "demoShow" },
    { key: "closeRate" },
  ];

  return (
    <>
      {notice ? (
        <Reason tone="warning" role="alert">
          {notice.text(t)}
        </Reason>
      ) : null}

      <section className="g-goals__section" aria-labelledby={`${baseId}-month`}>
        <h2 id={`${baseId}-month`} className="g-visually-hidden">
          {t("goalsSettings.month")}
        </h2>
        <div className="g-goals__top">
          <Select
            id={`${baseId}-month-select`}
            label={t("goalsSettings.month")}
            className="g-goals__month"
            value={month}
            onChange={onMonth}
            options={months.map((value) => ({ value, label: monthName(locale, value) }))}
            disabled={dirty || saving}
            hint={dirty ? t("goalsSettings.monthLocked") : undefined}
          />
          <TextField
            id={`${baseId}-average`}
            label={t("goalsSettings.averageSale")}
            hint={t("goalsSettings.averageSaleHint")}
            className="g-goals__amount"
            inputMode="decimal"
            autoComplete="off"
            value={draft.averageSale}
            onChange={(event) => change({ averageSale: event.target.value })}
            disabled={disabled}
            error={problemOf((problem) => problem.kind === "averageSale")}
          />
        </div>
      </section>

      <section className="g-goals__section" aria-labelledby={`${baseId}-goals`}>
        <h2 id={`${baseId}-goals`}>{t("goalsSettings.goalsTitle")}</h2>
        <p className="g-goals__muted">{t("goalsSettings.goalsHint", { month: name })}</p>
        {base.people.length === 0 ? <p className="g-goals__muted">{t("goalsSettings.noPeople")}</p> : null}
        <div className="g-goals__people">
          {base.people.map((row) => (
            <TextField
              key={row.person.membershipId}
              id={`${baseId}-goal-${row.person.membershipId}`}
              label={row.person.active ? row.person.displayName : t("goalsSettings.inactive", { name: row.person.displayName })}
              className="g-goals__amount"
              inputMode="decimal"
              autoComplete="off"
              placeholder={t("goalsSettings.noGoal")}
              value={draft.goals[row.person.membershipId] ?? ""}
              onChange={(event) => change({ goals: { [row.person.membershipId]: event.target.value } })}
              disabled={disabled}
              error={problemOf((problem) => problem.kind === "goal" && problem.membershipId === row.person.membershipId)}
            />
          ))}
        </div>
      </section>

      <section className="g-goals__section" aria-labelledby={`${baseId}-targets`}>
        <h2 id={`${baseId}-targets`}>{t("goalsSettings.targetsTitle")}</h2>
        <p className="g-goals__muted">{t("goalsSettings.targetsHint")}</p>
        <div className="g-goals__targets">
          {targetRows.map((row) =>
            "fixed" in row ? (
              <div key={row.fixed} className="g-goals__target" role="group" aria-labelledby={`${baseId}-target-${row.fixed}`}>
                <h3 id={`${baseId}-target-${row.fixed}`} className="g-goals__target-name">
                  {t(TARGET_NAMES[row.fixed])}
                </h3>
                <p className="g-goals__fixed">
                  <strong>{fixed(row.fixed)}</strong>
                  <span>{t("goalsSettings.decided")}</span>
                </p>
              </div>
            ) : (
              <div key={row.key} className="g-goals__target" role="group" aria-labelledby={`${baseId}-target-${row.key}`}>
                <h3 id={`${baseId}-target-${row.key}`} className="g-goals__target-name">
                  {t(TARGET_NAMES[row.key])}
                </h3>
                <div className="g-goals__range">
                  <TextField
                    id={`${baseId}-${row.key}-min`}
                    label={t("goalsSettings.from")}
                    className="g-goals__percent"
                    inputMode="decimal"
                    autoComplete="off"
                    value={draft.targets[row.key].min}
                    onChange={(event) => changeTarget(row.key, "min", event.target.value)}
                    disabled={disabled}
                    error={problemOf((problem) => (problem.kind === "percent" && problem.key === row.key && problem.field === "min") || (problem.kind === "minMissing" && problem.key === row.key))}
                  />
                  <TextField
                    id={`${baseId}-${row.key}-max`}
                    label={t("goalsSettings.to")}
                    className="g-goals__percent"
                    inputMode="decimal"
                    autoComplete="off"
                    value={draft.targets[row.key].max}
                    onChange={(event) => changeTarget(row.key, "max", event.target.value)}
                    disabled={disabled}
                    error={problemOf((problem) => (problem.kind === "percent" && problem.key === row.key && problem.field === "max") || (problem.kind === "minOverMax" && problem.key === row.key))}
                  />
                </div>
              </div>
            ),
          )}
        </div>
      </section>

      {dirty ? (
        <SaveBar
          summary={draftSummary(t, Math.max(1, changes.length))}
          problem={firstProblem ? problemText(t, firstProblem, nameOf) : undefined}
          onDiscard={discard}
          onSave={() => setConfirming({ error: null })}
          saveLabel={t("settings.draft.save")}
          saving={saving}
        />
      ) : null}

      {confirming && dirty && problems.length === 0 ? (
        <ConfirmChangesDialog changes={changes} error={confirming.error} saving={saving} onBack={() => setConfirming(null)} onApply={() => void save()} />
      ) : null}

      {blocker.status === "blocked" ? <LeaveDialog onStay={stay} onLeave={leave} /> : null}
    </>
  );
}
